'use client';

import { c as _c } from "react/compiler-runtime";
import { Fragment, type KeyboardEvent, type ReactNode, useState } from 'react';
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink';
import { VideoPlayerModal } from '@src/components/VideoPlayer/VideoPlayerModal';
import { type UseClickActionArgs } from '@src/hooks/useClickAction/useClickActionArgs';
import { useClickActionContext } from '@src/hooks/useClickAction/useClickActionContext';
import { useOnClick } from '@src/hooks/useClickAction/useOnClick';
import { useLocale } from '@src/i18n/LocaleProvider';
import { useURL } from '@src/utils/URLProvider';
import { filterBuilderAttributes, getBuilderPreviewMode } from '@src/utils/builder.util';
import { LoginModal } from './LoginModal';
export const useClickAction = args => {
  const $ = _c(40);
  const {
    actionName: t0,
    popoutVideo,
    navigate,
    attributes
  } = args;
  const actionName = t0 === undefined ? "none" : t0;
  const {
    locale
  } = useLocale();
  const videoId = popoutVideo?.videoId;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getBuilderPreviewMode();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    edit
  } = t1;
  const {
    href
  } = useURL();
  const [isLoading, setIsLoading] = useState(false);
  const [isVideoOpen, setVideoOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const {
    isLoadingAnyAction
  } = useClickActionContext();
  const shouldSkipOnClick = edit || isLoading || isLoadingAnyAction || actionName === "none";
  let t2;
  if ($[1] !== args || $[2] !== shouldSkipOnClick) {
    t2 = {
      setVideoOpen,
      setIsLoginModalOpen,
      setIsLoading,
      shouldSkip: shouldSkipOnClick,
      useClickActionArgs: args
    };
    $[1] = args;
    $[2] = shouldSkipOnClick;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    onClick
  } = useOnClick(t2);
  let t3;
  if ($[4] !== args.loginModal?.redirectUrl || $[5] !== href || $[6] !== locale) {
    t3 = () => {
      const currentUrl = new URL(href);
      currentUrl.pathname = `${locale.path}${args.loginModal?.redirectUrl}`;
      return currentUrl.toString();
    };
    $[4] = args.loginModal?.redirectUrl;
    $[5] = href;
    $[6] = locale;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const getLoginModalRedirectUrl = t3;
  let t4;
  if ($[8] !== onClick) {
    t4 = async e => {
      if (e.code === "Enter" || e.code === "Space") {
        await onClick?.();
      }
    };
    $[8] = onClick;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const onKeyDown = t4;
  const isWrapping = actionName === "navigate";
  let t5;
  if ($[10] !== isVideoOpen || $[11] !== videoId) {
    t5 = () => <>{videoId && <VideoPlayerModal autoplay={true} isOpen={isVideoOpen} onClose={() => {
        setVideoOpen(false);
      }} videoId={videoId} />}</>;
    $[10] = isVideoOpen;
    $[11] = videoId;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const renderVideo = t5;
  let t6;
  if ($[13] !== args.loginModal?.disclaimer || $[14] !== args.loginModal?.heading || $[15] !== args.loginModal?.isPreviewing || $[16] !== args.loginModal?.redirectUrl || $[17] !== args.loginModal?.subHeading || $[18] !== getLoginModalRedirectUrl || $[19] !== isLoginModalOpen) {
    t6 = () => <>{args.loginModal?.redirectUrl && <LoginModal redirectUrl={getLoginModalRedirectUrl()} heading={args.loginModal?.heading} subHeading={args.loginModal?.subHeading} disclaimer={args.loginModal?.disclaimer} isOpen={isLoginModalOpen || Boolean(edit && args.loginModal?.isPreviewing)} onClose={() => setIsLoginModalOpen(false)} />}</>;
    $[13] = args.loginModal?.disclaimer;
    $[14] = args.loginModal?.heading;
    $[15] = args.loginModal?.isPreviewing;
    $[16] = args.loginModal?.redirectUrl;
    $[17] = args.loginModal?.subHeading;
    $[18] = getLoginModalRedirectUrl;
    $[19] = isLoginModalOpen;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const renderLoginModal = t6;
  let t7;
  if ($[21] !== attributes || $[22] !== isWrapping || $[23] !== navigate?.href || $[24] !== navigate?.target || $[25] !== onClick || $[26] !== onKeyDown || $[27] !== renderLoginModal || $[28] !== renderVideo) {
    t7 = t8 => {
      const {
        children
      } = t8;
      const usingCartFlyout = navigate?.href === "/cart";
      const wrapperProps = !isWrapping || usingCartFlyout ? {} : {
        ...(filterBuilderAttributes(attributes) as any),
        href: String(navigate?.href),
        target: navigate?.target,
        onClick,
        onKeyDown
      };
      const Wrapper = isWrapping && !usingCartFlyout ? LocalizedLink : Fragment;
      return <Wrapper {...wrapperProps}>{children}{renderVideo()}{renderLoginModal()}</Wrapper>;
    };
    $[21] = attributes;
    $[22] = isWrapping;
    $[23] = navigate?.href;
    $[24] = navigate?.target;
    $[25] = onClick;
    $[26] = onKeyDown;
    $[27] = renderLoginModal;
    $[28] = renderVideo;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const ClickWrapper = t7;
  let t8;
  if ($[30] !== onClick || $[31] !== onKeyDown || $[32] !== shouldSkipOnClick) {
    t8 = shouldSkipOnClick ? {} : {
      onClick,
      onKeyDown
    };
    $[30] = onClick;
    $[31] = onKeyDown;
    $[32] = shouldSkipOnClick;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  const handlers = t8;
  let t9;
  if ($[34] !== ClickWrapper || $[35] !== handlers || $[36] !== isLoading || $[37] !== isWrapping || $[38] !== renderVideo) {
    t9 = {
      isWrapping,
      handlers,
      isLoading,
      ClickWrapper,
      renderVideo
    };
    $[34] = ClickWrapper;
    $[35] = handlers;
    $[36] = isLoading;
    $[37] = isWrapping;
    $[38] = renderVideo;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
};